<template>
  <InputDefault
    v-bind="$attrs"
    v-model="model"
    pre-icon="CalendarIcon"
    :placeholder="placeholder"
    :label="label"
    :estilo-activo-readonly="!estaBloqueado()"
    :required="required"
    :enable-copy-paste="enableCopyPaste"
    type="text"
    :readonly="true"
    @click="openCalendarClick()"
    @paste="handlePaste"
  >
    <template #limpiar="{ estilo }">
      <button
        v-if="clear && model && !estaBloqueado()"
        type="button"
        aria-label="Limpiar fecha"
        class="absolute size-6 flex items-center justify-center cursor-pointer rounded-md border border-transparent bg-blue-50 text-blue-600 hover:bg-blue-100"
        :style="estilo"
        @click.stop="limpiarFecha"
      >
        <XMarkIcon class="size-4" />
      </button>
    </template>
  </InputDefault>
  <!-- Teleport + fixed: dentro de un modal con overflow el calendario quedaba recortado -->
  <Teleport to="body">
  <div
    v-if="!$attrs.withoutpicker"
    :id="'calendar_content_' + $attrs.id"
    class="fixed z-1000 w-auto h-auto rounded-xl shadow-[0_10px_40px_10px_rgba(0,0,0,0.08)] dark:bg-neutral-800 hidden"
  >
    <div
      :id="'calendar_' + $attrs.id"
      class="w-66 flex flex-col bg-white border border-gray-400 shadow-lg rounded-xl overflow-hidden dark:bg-neutral-900 dark:border-neutral-700"
    >
      <VueDatePicker
        :id="'datepicker_' + $attrs.id"
        v-model="modelPicker"
        format="yyyy-MM-dd"
        locale="es-CO"
        hide-offset-dates
        inline
        auto-apply
        :min-date="blockAfterDates ? new Date() : undefined"
        :max-date="blockFutureDates ? new Date() : undefined"
        :enable-time-picker="enableTimePicker"
        :range="range"
        @update:model-value="
          async ($event: Event) => {
            if (estaBloqueado() || !$event || !modelPicker) return
            model = range
              ? (modelPicker as Date[])
                  .map((item: Date) =>
                    enableTimePicker ? item?.yyyymmddhhmm() : item?.yyyymmdd()
                  )
                  .join(',')
              : enableTimePicker
                ? (modelPicker as Date).yyyymmddhhmm()
                : (modelPicker as Date).yyyymmdd()
            await nextTick()
            Object.keys(eventHandlers).forEach((evento: string) => {
              eventHandlers[evento]($event)
            })

            if (onChange) onChange(model)
            if (closeOnSelect) closeCalendarClick()
          }
        "
      />
      <div
        class="flex justify-end items-center gap-x-2 p-3 border-t border-gray-400 dark:border-neutral-700"
      >
        <button
          type="button"
          class="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-gray-400 bg-white text-gray-800 shadow-sm hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-900 dark:border-neutral-700 dark:text-white dark:hover:bg-neutral-800"
          @click="closeCalendarClick"
        >
          Cerrar
        </button>
      </div>
    </div>
  </div>
  </Teleport>
  <!-- End Datepicker -->
</template>
<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, useAttrs, watch } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import type { ModelValue } from '@/modules/Core/usings/constantes'
import { getEventHandlers } from '@/modules/Core/usings/generales'
import type { IConfCompxfuncion } from '@/modules/Administracion/interfaces'

dayjs.extend(customParseFormat)

const attrs = useAttrs()
const model = defineModel<ModelValue>()
const modelPicker = defineModel<Date | Date[]>('modelPicker')

const props = defineProps({
  estiloActivoReadonly: {
    type: Boolean,
    default: false
  },
  label: {
    type: String,
    default: 'Fecha'
  },
  required: {
    type: Boolean,
    default: false
  },
  isError: {
    type: Boolean,
    default: false
  },
  clear: {
    type: Boolean,
    default: false
  },
  clearEvent: {
    type: Function,
    default: () => {}
  },
  blockFutureDates: {
    type: Boolean,
    default: false
  },
  blockAfterDates: {
    type: Boolean,
    default: false
  },
  closeOnSelect: {
    type: Boolean,
    default: true
  },
  enableTimePicker: {
    type: Boolean,
    default: false
  },
  range: {
    type: Boolean,
    default: false
  },
  onChange: {
    type: Function,
    default: () => {}
  },
  compxfunciones: {
    type: Array<IConfCompxfuncion>,
    default: () => []
  },
  enableCopyPaste: {
    type: Boolean,
    default: false
  }
})
const { range, enableTimePicker } = props
const placeholder = ref('')
function estaBloqueado() {
  const deshabilitado = attrs.disabled === '' || Boolean(attrs.disabled)
  const soloLectura = attrs.readonly === '' || Boolean(attrs.readonly)
  return deshabilitado || Boolean(attrs.withoutpicker) || (soloLectura && !props.estiloActivoReadonly)
}
const eventHandlers = computed(() => {
  let handlers: Record<string, (e: Event) => void> = {}

  if (props.compxfunciones && props.compxfunciones.length > 0) {
    handlers = getEventHandlers(
      typeof props.compxfunciones === 'string'
        ? JSON.parse(props.compxfunciones)
        : props.compxfunciones
    )
  }
  return handlers
})
watch(model, () => {
  if (range) {
    modelPicker.value = model.value
      ? (model.value as string)
          .split(',')
          .map(
            (item: string) =>
              dayjs(enableTimePicker ? item : item.slice(0, 10) + ' 12:00:00').toDate()
          )
      : []
    placeholder.value = enableTimePicker
      ? 'YYYY-MM-DD HH:MM,YYYY-MM-DD HH:MM'
      : 'YYYY-MM-DD,YYYY-MM-DD'
  } else {
    modelPicker.value = model.value
      ? dayjs(enableTimePicker ? model.value as string : (model.value as string).slice(0, 10) + ' 12:00:00').toDate()
      : dayjs().toDate()
    placeholder.value = 'YYYY-MM-DD' + (enableTimePicker ? ', HH:mm' : '')
  }
}, { immediate: true })

// fixed no sigue al input: se cierra si la página o el modal hacen scroll
const cerrarAlDesplazar = (e: Event) => {
  const el = document.getElementById('calendar_content_' + attrs.id)
  if (el && !el.contains(e.target as Node)) closeCalendarClick()
}

onMounted(() => {
  window.addEventListener('scroll', cerrarAlDesplazar, true)
  window.addEventListener('resize', closeCalendarClick)
  window.addEventListener('click', function (e) {
    const datePickerCont = document.getElementById(
      'calendar_content_' + attrs.id
    )
    const datePickerCale = document.getElementById('calendar_' + attrs.id)
    const datePickerPicker = document.getElementById('datepicker_' + attrs.id)
    const datePickerInput = document.getElementById(attrs.id as string)
    if (
      datePickerCont &&
      datePickerInput &&
      datePickerCale &&
      datePickerPicker
    ) {
      const tabsvali = false
      if (
        datePickerCont != e.target &&
        datePickerInput != e.target &&
        datePickerCale != e.target &&
        datePickerPicker != e.target &&
        !tabsvali
      ) {
        datePickerCont.classList.add('hidden')
      }
    }
  })
})

onUnmounted(() => {
  window.removeEventListener('scroll', cerrarAlDesplazar, true)
  window.removeEventListener('resize', closeCalendarClick)
})

function openCalendarClick() {
  if (estaBloqueado()) return
  const el = document.getElementById('calendar_content_' + attrs.id)
  const input = document.getElementById(attrs.id as string)
  if (!el || !input) return
  el.classList.remove('hidden')
  const rect = input.getBoundingClientRect()
  const abrirArriba = window.innerHeight - rect.bottom < el.offsetHeight && rect.top > el.offsetHeight
  el.style.left = `${Math.min(rect.left, window.innerWidth - el.offsetWidth - 8)}px`
  el.style.top = `${abrirArriba ? rect.top - el.offsetHeight : rect.bottom}px`
}
function limpiarFecha() {
  if (estaBloqueado()) return
  model.value = ''
  props.clearEvent()
}
function closeCalendarClick() {
  const el = document.getElementById('calendar_content_' + attrs.id)
  if (el) el.classList.add('hidden')
}

function handlePaste(e: ClipboardEvent | string) {
  if (estaBloqueado()) return
  let pastedText = ''

  if (e instanceof ClipboardEvent) {
    e.preventDefault()
    pastedText = e.clipboardData?.getData('text') || ''
  } else {
    pastedText = e
  }

  if (!pastedText) return

  const textoLimpio = pastedText.trim()
  const formatoISO = /^(\d{4})-(\d{2})-(\d{2})$/
  const formatoDMY = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/

  let year: string, month: string, day: string

  const matchISO = textoLimpio.match(formatoISO)
  const matchDMY = textoLimpio.match(formatoDMY)

  if (matchISO) {
    year = matchISO[1]
    month = matchISO[2]
    day = matchISO[3]
  } else if (matchDMY) {
    day = matchDMY[1].padStart(2, '0')
    month = matchDMY[2].padStart(2, '0')
    year = matchDMY[3]
  } else {
    window.notificacion.notificacion(
      'failure',
      'Formato de fecha inválido. Ejemplos válidos: 2025-12-31 o 31/12/2025'
    )
    return
  }

  const fechaValidar = dayjs(`${year}-${month}-${day}`, 'YYYY-MM-DD', true)
  
  if (!fechaValidar.isValid()) {
    window.notificacion.notificacion(
      'failure',
      'Fecha inválida. Verifique que el día, mes y año sean correctos'
    )
    return
  }

  if (range) {
    modelPicker.value = [fechaValidar.toDate()]
    model.value = [fechaValidar]
      .map((item) =>
        enableTimePicker ? item?.format('YYYY-MM-DD HH:mm') : item?.format('YYYY-MM-DD')
      )
      .join(',')
  } else {
    modelPicker.value = fechaValidar.toDate()
    model.value = enableTimePicker ? fechaValidar?.format('YYYY-MM-DD HH:mm') : fechaValidar?.format('YYYY-MM-DD')
  }

  window.notificacion.notificacion('success', 'Fecha pegada correctamente')
}
</script>
<style>
:root {
  --dp-input-padding: 0.6rem 0.2rem;
}
.custom-select {
  cursor: pointer;
  color: var(--vp-c-text-2);
  margin: 0;
  display: inline-block;
}
.dp__relative {
  position: none !important;
}
</style>
